import React, { useState, useMemo } from "react";
import { useGlobalData } from "../hooks/useGlobalHooks";
import { LiveLTPCell } from "./OptimizedCell";
import { Pencil } from "lucide-react";
import BulkModifyModal from "./BulkModifyModal";
import { toast } from "react-hot-toast";
import { getStockFromSymbol } from "../hooks/useStockOptionsView";
import { getCommodityOptionFromSymbol } from "../hooks/useCommodityOptionsView";

/**
 * StockCommodityOptionsPositionComparisonModal
 * UI exactly matches PositionComparisonModal.
 * Pass category="stock" or category="commodity".
 */
export function StockCommodityOptionsPositionComparisonModal({
  isOpen,
  onClose,
  clientData,
  orders,
  positions,
  symbolStrategyBaseQty,
  onCancel,
  category = "stock",
}) {
  const { indexMultipliers } = useGlobalData();

  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [modifyModalOpen, setModifyModalOpen] = useState(false);
  const [ordersToModify, setOrdersToModify] = useState([]);

  const isStock = category === "stock";
  const modalTitle = isStock
    ? "Stock Options – Position Comparison"
    : "Commodity Options – Position Comparison";

  function getSegmentName(symbol) {
    if (!symbol) return null;
    if (isStock) return getStockFromSymbol(symbol);
    return getCommodityOptionFromSymbol(symbol);
  }

  function extractStrike(symbol) {
    const match = symbol?.match(/(\d{3,6})(CE|PE)$/);
    return match ? match[1] : "N/A";
  }

  function formatTime(isoString) {
    if (!isoString) return "-";
    try { return new Date(isoString).toLocaleTimeString("en-IN", { hour12: false }); }
    catch (e) { return isoString; }
  }

  function formatPrice(num) {
    if (num === undefined || num === null || num === 0) return "-";
    return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function getMultiplier(segmentName) {
    if (!indexMultipliers || !clientData?.clientId) return 1;
    const matches = indexMultipliers.filter(
      (m) => m.client_id === clientData.clientId && m.index === segmentName
    );
    const mult = matches.find((m) =>
      ["dhan_xts", "dhan_xts_anurag"].includes((m.broker || "").toLowerCase())
    ) || matches[0];
    return mult && !isNaN(parseFloat(mult.multiplier)) ? parseFloat(mult.multiplier) : 1;
  }

  const positionsBySymbolStrategy = useMemo(() => {
    if (!positions || !clientData?.clientId)
      return { rows: [], matchedOrderIds: new Set() };

    const matchedOrderIds = new Set();
    const rows = [];
    const processedKeys = new Set();
    const clientId = (clientData.clientId || "").toString().trim();

    const clientPositions = positions.filter(
      (p) => (p.client_id || p.clientId || "").toString().trim() === clientId && getSegmentName(p.symbol) !== null
    );

    clientPositions.forEach((pos) => {
      const key = `${pos.symbol}_${pos.strategy || "NO_STRATEGY"}`;
      if (processedKeys.has(key)) return;
      processedKeys.add(key);

      let actualQty = 0;
      clientPositions.forEach((p) => {
        if (`${p.symbol}_${p.strategy || "NO_STRATEGY"}` === key)
          actualQty += Math.abs(parseFloat(p.net_qty || p.qty || 0));
      });

      const baseQtyInfo = symbolStrategyBaseQty ? symbolStrategyBaseQty[key] : null;
      const baseQty = baseQtyInfo ? baseQtyInfo.baseQty : actualQty;
      const segmentName = getSegmentName(pos.symbol) || (isStock ? "STOCK" : "COMMODITY");
      const rawMult = getMultiplier(segmentName.toUpperCase());
      const multiplier = (rawMult === "N/A" || typeof rawMult !== "number" || isNaN(rawMult)) ? 1 : rawMult;
      const expectedQty = multiplier * baseQty;

      let matchingOrders = [];
      if (orders) {
        matchingOrders = orders.filter((o) => {
          const oClientId = (o.clientId || o.client_id || "").toString().trim();
          if (oClientId !== clientId) return false;
          if (getSegmentName(o.symbol) === null) return false;
          const cleanPos = (pos.symbol || "").replace(/^(MCX|NSE|BSE|NFO|BFO):/i, "").trim().toUpperCase();
          const cleanOrd = (o.symbol || "").replace(/^(MCX|NSE|BSE|NFO|BFO):/i, "").trim().toUpperCase();
          if (cleanPos !== cleanOrd) return false;
          const curStrat = (o.strategy || "NO_STRATEGY").toString().trim().toUpperCase();
          const tgtStrat = (pos.strategy || "NO_STRATEGY").toString().trim().toUpperCase();
          if (curStrat !== tgtStrat) return false;
          const oSide = String(o.side || "").toLowerCase();
          const isSellSide = oSide === "-1" || oSide === "2" || oSide === "sell" || oSide === "s";
          if (!isSellSide) return false;
          const oStatus = (o.status || "").toLowerCase();
          const oStatusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;
          const isPending = oStatus.includes("pending") || oStatus.includes("open") || oStatusNo === 6;
          return isPending;
        });
      }

      const totalSlQty = matchingOrders.reduce((acc, o) => acc + (parseInt(o.qty || o.quantity || 0) || 0), 0);
      matchingOrders.forEach((o) => matchedOrderIds.add(o.orderid || o.order_id));

      rows.push({
        symbol: pos.symbol,
        strategy: pos.strategy || "N/A",
        index: segmentName,
        baseQty,
        multiplier,
        expectedQty,
        actualQty,
        buyPrice: pos.net_avg || pos.avg_price || 0,
        match: actualQty === expectedQty,
        clientsTrading: baseQtyInfo ? baseQtyInfo.clients : 1,
        strike: extractStrike(pos.symbol),
        matchingPositions: [pos],
        matchingOrders,
        totalSlQty,
      });
    });

    // Orphan pending sell orders
    if (orders) {
      orders.forEach((o) => {
        const oClientId = (o.clientId || o.client_id || "").toString().trim();
        if (oClientId !== clientId) return;
        const oSide = String(o.side || "").toLowerCase();
        const oStatus = (o.status || "").toLowerCase();
        const oStatusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;
        const isSell = oSide === "-1" || oSide === "2" || oSide === "sell" || oSide === "s";
        const isPending = oStatus.includes("pending") || oStatus.includes("open") || oStatusNo === 6;
        const orderId = o.orderid || o.order_id;
        const isMatched = matchedOrderIds.has(orderId);
        const segResult = getSegmentName(o.symbol);
        if (isSell && isPending && !isMatched && segResult) {
          rows.push({
            symbol: o.symbol, strategy: o.strategy || "Unknown",
            index: segResult, baseQty: 0, multiplier: 1,
            expectedQty: 0, actualQty: 0, match: false, clientsTrading: [],
            strike: extractStrike(o.symbol), matchingOrders: [o],
            totalSlQty: parseInt(o.qty || o.quantity || 0) || 0, isOrphan: true,
          });
        }
      });
    }

    return { rows, matchedOrderIds };
  }, [positions, clientData, symbolStrategyBaseQty, orders, category]);

  const [selectedAsset, setSelectedAsset] = useState("ALL");

  const uniqueAssets = useMemo(() => {
    const rows = positionsBySymbolStrategy.rows || [];
    const assets = new Set(rows.map((r) => r.index).filter(Boolean));
    return ["ALL", ...Array.from(assets).sort()];
  }, [positionsBySymbolStrategy]);

  const filteredRows = useMemo(() => {
    const rows = positionsBySymbolStrategy.rows || [];
    if (selectedAsset === "ALL") return rows;
    return rows.filter((r) => r.index === selectedAsset);
  }, [positionsBySymbolStrategy, selectedAsset]);

  const openCancelModal = (ordersList) => { if (onCancel) onCancel(ordersList); };

  const openModifyModal = (orderList) => {
    const enriched = orderList.map((o) => {
      const row = filteredRows.find((r) =>
        r.matchingOrders?.some((mo) => (mo.order_id || mo.orderId) === (o.order_id || o.orderId))
      );
      return { ...o, expectedQty: row?.expectedQty };
    });
    setOrdersToModify(enriched);
    setModifyModalOpen(true);
  };

  const toggleOrderSelection = (orderId) => {
    const next = new Set(selectedOrderIds);
    if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
    setSelectedOrderIds(next);
  };

  const handleBulkModify = () => {
    const selectedOrders = [];
    filteredRows.forEach((row) => {
      if (row.matchingOrders) {
        row.matchingOrders.forEach((o) => {
          if (selectedOrderIds.has(o.order_id || o.orderId)) selectedOrders.push(o);
        });
      }
    });
    if (selectedOrders.length === 0) { toast.error("Please select at least one order to modify"); return; }
    openModifyModal(selectedOrders);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="bg-[#1a1d29] rounded-lg shadow-xl max-w-[90vw] w-full max-h-[80vh] overflow-hidden flex flex-col border border-gray-700/50" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="bg-[#14161f] px-6 py-4 border-b border-gray-700 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-white">{modalTitle}</h2>
              {selectedOrderIds.size > 0 && (
                <button onClick={handleBulkModify} className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded transition-all shadow-lg flex items-center gap-2">
                  Modify Selected ({selectedOrderIds.size})
                </button>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Client Info */}
          <div className="bg-[#1a1d29] px-6 py-3 border-b border-gray-700/50">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-400">Client:</span>
              <span className="text-white font-semibold">{clientData?.clientName || clientData?.clientId}</span>
              <span className="text-gray-400">({clientData?.clientId})</span>
            </div>
          </div>
          {/* Content */}
          <div className="flex-1 overflow-auto">
            {positionsBySymbolStrategy.rows?.length === 0 ? (
              <div className="text-center py-8 px-6">
                <p className="text-gray-400 mb-4">No positions found for comparison</p>
                <div className="text-left bg-red-900/20 border border-red-700/30 rounded p-4 max-w-2xl mx-auto text-xs">
                  <p className="text-red-400 font-semibold mb-2">Debug Info:</p>
                  <p className="text-gray-300">• Total positions available: {positions?.length || 0}</p>
                  <p className="text-gray-300">• Client ID: {clientData?.clientId || "N/A"}</p>
                  <p className="text-gray-300">• Client Name: {clientData?.clientName || "N/A"}</p>
                  <p className="text-gray-300">• Symbol+Strategy base qty entries: {Object.keys(symbolStrategyBaseQty || {}).length}</p>
                  <p className="text-yellow-400 mt-2">💡 Open browser console (F12) for detailed logs</p>
                </div>
              </div>
            ) : (
              <div className="bg-[#14161f] overflow-hidden">
                <div className="overflow-auto max-h-[calc(80vh-280px)]">
                  <table className="w-full text-sm">
                    <thead className="bg-[#0f1117] sticky top-0 z-10">
                      <tr className="border-b border-gray-700/50">
                        <th colSpan="11" className="text-center py-2 bg-[#14161f] text-gray-400 font-semibold text-xs uppercase tracking-wider border-r border-gray-700/50">Position Comparison</th>
                        <th colSpan="7" className="text-center py-2 bg-red-900/10 text-red-400 font-semibold text-xs uppercase tracking-wider">Matching Sell Orders</th>
                      </tr>
                      <tr className="border-b border-gray-700/30">
                        <th className="w-8 px-2 text-center">
                          <input type="checkbox" className="w-4 h-4 rounded border-gray-700 bg-gray-900 text-blue-600 focus:ring-blue-500"
                            checked={filteredRows.some((r) => r.matchingOrders?.length > 0) && filteredRows.every((r) => !r.matchingOrders || r.matchingOrders.every((o) => selectedOrderIds.has(o.order_id || o.orderId)))}
                            onChange={(e) => {
                              const allIds = new Set(selectedOrderIds);
                              filteredRows.forEach((r) => { if (r.matchingOrders) { r.matchingOrders.forEach((o) => { if (e.target.checked) allIds.add(o.order_id || o.orderId); else allIds.delete(o.order_id || o.orderId); }); } });
                              setSelectedOrderIds(allIds);
                            }} />
                        </th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">
                          <div className="flex items-center gap-2">
                            <span>{isStock ? "Stock" : "Commodity"}</span>
                            <select value={selectedAsset} onChange={(e) => setSelectedAsset(e.target.value)} onClick={(e) => e.stopPropagation()} className="bg-[#1a1d29] border border-gray-700 text-gray-300 text-xs rounded px-1 py-0.5 focus:outline-none focus:border-blue-500">
                              {uniqueAssets.map((asset) => (<option key={asset} value={asset}>{asset === "ALL" ? "All" : asset.toUpperCase()}</option>))}
                            </select>
                          </div>
                        </th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Symbol</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Strategy</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Strike</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Base Qty (1x)</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Multiplier</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Expected Qty</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Actual Qty</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Buy Price</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium border-r border-gray-700/50">LTP</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium border-r border-gray-700/50">Match</th>
                        <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">Order ID</th>
                        <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">Time</th>
                        <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">Order Qty</th>
                        <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">Stop Price</th>
                        <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">Limit Price</th>
                        <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">Total Sell Qty</th>
                        <th className="text-center py-3 px-4 text-red-300 font-medium bg-red-900/5 border-l border-gray-700/30">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows?.map((row, idx) => {
                        const isMatch = row.actualQty === row.expectedQty;
                        const qtyColor = isMatch ? "text-green-400" : "text-red-400";
                        const hasOrders = row.matchingOrders && row.matchingOrders.length > 0;
                        return (
                          <tr key={idx} className="border-b border-gray-800/30 hover:bg-[#1a1d29] transition-colors valign-top">
                            <td className="px-2 text-center align-middle">
                              {hasOrders && (
                                <div className="flex flex-col gap-1 items-center">
                                  {row.matchingOrders.map((o, i) => (
                                    <div key={i} className="h-[20px] flex items-center">
                                      <input type="checkbox" className="w-3.5 h-3.5 rounded border-gray-800 bg-gray-950 text-blue-600 focus:ring-blue-500"
                                        checked={selectedOrderIds.has(o.order_id || o.orderId)}
                                        onChange={() => toggleOrderSelection(o.order_id || o.orderId)} />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="py-2.5 px-4"><span className="text-blue-400 text-xs font-semibold">{(row.index || "").toUpperCase()}</span></td>
                            <td className="py-2.5 px-4 text-gray-300 text-xs font-mono">{row.symbol}</td>
                            <td className="py-2.5 px-4 text-gray-400 text-xs">{row.strategy}</td>
                            <td className="py-2.5 px-4 text-center text-gray-300">{row.strike}</td>
                            <td className="py-2.5 px-4 text-center text-white font-medium">{row.isOrphan ? "-" : row.baseQty}</td>
                            <td className="py-2.5 px-4 text-center text-purple-400 font-medium">{row.isOrphan ? "-" : (Number.isFinite(Number(row.multiplier)) ? `${row.multiplier}x` : "-")}</td>
                            <td className="py-2.5 px-4 text-center text-yellow-400 font-medium">{row.isOrphan ? "-" : (Number.isFinite(row.expectedQty) ? row.expectedQty : "-")}</td>
                            <td className={`py-2.5 px-4 text-center font-bold ${qtyColor}`}>{row.isOrphan ? "-" : row.actualQty}</td>
                            <td className="py-2.5 px-4 text-center text-gray-300 font-medium">{row.isOrphan ? "-" : formatPrice(row.buyPrice)}</td>
                            <td className="py-2.5 px-4 text-center text-blue-400 font-medium"><LiveLTPCell symbol={row.symbol} formatFn={formatPrice} /></td>
                            <td className="py-2.5 px-4 text-center border-r border-gray-700/50">
                              {row.isOrphan ? <span className="text-yellow-500" title="Orphaned Order">⚠️</span> : isMatch ? <span className="text-green-400">✓</span> : <span className="text-red-400">✗</span>}
                            </td>
                            <td className="py-2.5 px-4 text-center text-gray-300 text-xs font-mono border-l border-gray-700/50 align-top">
                              {hasOrders ? (<div className="flex flex-col gap-1">{row.matchingOrders.map((o, i) => (<div key={i} className="whitespace-nowrap flex items-center justify-center gap-2 text-[10px]"><span className={o.status === 6 ? "text-blue-300" : "text-gray-400"}>{o.order_id || o.orderId || "N/A"}</span></div>))}</div>) : <span className="text-gray-600">-</span>}
                            </td>
                            <td className="py-2.5 px-4 text-center text-gray-300 text-xs font-mono border-l border-gray-700/30 align-top">
                              {hasOrders ? (<div className="flex flex-col gap-1">{row.matchingOrders.map((o, i) => (<div key={i} className="text-gray-400 text-[10px]">{formatTime(o.orderdatetime || o.order_datetime || o.orderDateTime || o.orderTime)}</div>))}</div>) : <span className="text-gray-600">-</span>}
                            </td>
                            <td className="py-2.5 px-4 text-center text-gray-300 font-medium border-l border-gray-700/30 align-top">
                              {hasOrders ? (<div className="flex flex-col gap-1">{row.matchingOrders.map((o, i) => (<div key={i} className="text-red-300">{o.qty}</div>))}</div>) : <span className="text-gray-600">-</span>}
                            </td>
                            <td className="py-2.5 px-4 text-center text-gray-300 font-medium border-l border-gray-700/30 align-top">
                              {hasOrders ? (<div className="flex flex-col gap-1">{row.matchingOrders.map((o, i) => (<div key={i}>{o.stopPrice || o.stop_price || "-"}</div>))}</div>) : <span className="text-gray-600">-</span>}
                            </td>
                            <td className="py-2.5 px-4 text-center text-gray-300 font-medium border-l border-gray-700/30 align-top">
                              {hasOrders ? (<div className="flex flex-col gap-1">{row.matchingOrders.map((o, i) => (<div key={i}>{o.price || o.limitPrice || o.limit_price || "-"}</div>))}</div>) : <span className="text-gray-600">-</span>}
                            </td>
                            <td className="py-2.5 px-4 text-center text-red-300 font-bold font-mono border-l border-gray-700/50">{row.totalSlQty}</td>
                            <td className="py-2.5 px-4 text-center border-l border-gray-700/50 align-middle">
                              {hasOrders ? (
                                <div className="flex flex-row items-center justify-center gap-3">
                                  <div className="flex flex-col gap-1 items-center">
                                    {row.matchingOrders.map((o, i) => (
                                      <div key={i} className="h-[20px] flex items-center gap-1">
                                        <button onClick={(e) => { e.stopPropagation(); openModifyModal([o]); }} className="text-blue-500/70 hover:text-blue-400 hover:bg-blue-500/10 p-1.5 rounded transition-all group border border-transparent hover:border-blue-500/30" title={`Modify Order ${o.order_id || o.orderId}`}>
                                          <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); openCancelModal([o]); }} className="text-red-500/70 hover:text-red-400 hover:bg-red-500/10 p-1.5 rounded transition-all group border border-transparent hover:border-red-500/30" title={`Cancel Order ${o.order_id || o.orderId}`}>
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                  {row.matchingOrders.length > 1 && (
                                    <div className="flex flex-col gap-2">
                                      <button onClick={(e) => { e.stopPropagation(); openCancelModal(row.matchingOrders); }} className="text-[9px] font-bold text-red-400 hover:text-white bg-red-950/30 hover:bg-red-600 border border-red-900/50 hover:border-red-500 px-2 py-1 rounded transition-all whitespace-nowrap shadow-sm" title="Cancel All">Cancel All</button>
                                      <button onClick={(e) => { e.stopPropagation(); openModifyModal(row.matchingOrders); }} className="text-[9px] font-bold text-blue-400 hover:text-white bg-blue-950/30 hover:bg-blue-600 border border-blue-900/50 hover:border-blue-500 px-2 py-1 rounded transition-all whitespace-nowrap shadow-sm" title="Modify All">Modify All</button>
                                    </div>
                                  )}
                                </div>
                              ) : <span className="text-gray-600">-</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <BulkModifyModal isOpen={modifyModalOpen} onClose={() => setModifyModalOpen(false)} orders={ordersToModify} onSuccess={() => setSelectedOrderIds(new Set())} />
    </>
  );
}
